// pdfjs-dist v4 (pulled in by react-pdf-highlighter-plus) calls
// `Promise.withResolvers()` inside PDFDocumentLoadingTask / getDocument. Two
// things can make it missing:
//   1. Older browsers simply don't have it.
//   2. `@yapi/dataforms-components.viewer-web-component` (Angular Elements,
//      imported at this app's entry) bundles core-js/zone.js polyfills that
//      REPLACE the global `Promise` at import time — wiping out any earlier
//      patch on the native Promise.
// So this is a callable, idempotent function rather than a one-shot side effect:
// it's invoked again from the PDF viewer chunk (which loads AFTER that web
// component) right before pdfjs runs, so it patches whatever Promise is current.
type WithResolvers = {
  withResolvers?: <T>() => {
    promise: Promise<T>;
    resolve: (value: T | PromiseLike<T>) => void;
    reject: (reason?: unknown) => void;
  };
};

export function ensureWithResolvers(): void {
  const P = Promise as PromiseConstructor & WithResolvers;
  if (typeof P.withResolvers !== "function") {
    P.withResolvers = function withResolvers<T>() {
      let resolve!: (value: T | PromiseLike<T>) => void;
      let reject!: (reason?: unknown) => void;
      const promise = new Promise<T>((res, rej) => {
        resolve = res;
        reject = rej;
      });
      return { promise, resolve, reject };
    };
  }
}

// Apply once on module load for any import graph without the web component.
ensureWithResolvers();

// pdf.js (via react-pdf-highlighter-plus) observes its container with
// ResizeObserver and, on a viewport/DPR/dimensions change, adjusts layout inside
// the callback — which schedules another resize. The browser reports the
// undelivered observations as the (harmless) uncaught error
// "ResizeObserver loop completed with undelivered notifications", which trips
// webpack-dev-server's full-screen dev overlay.
//
// We can't reliably swallow it with a window "error" listener: for an uncaught
// error `window` is the event target, so listeners fire in registration order
// (the capture flag gives no priority there), and the dev-server client entry
// registers its listener before any app code runs. So instead we PREVENT the
// error at its source — deferring each ResizeObserver callback to the next
// animation frame breaks the synchronous resize→resize loop, so the notification
// is always delivered and the error never fires. This runs at entry import, well
// before pdf.js constructs any ResizeObserver.
export function patchResizeObserverLoop(): void {
  if (typeof window === "undefined" || !window.ResizeObserver) {
    return;
  }
  const Native = window.ResizeObserver as typeof ResizeObserver & {
    __yapiRoLoopPatched?: boolean;
  };
  if (Native.__yapiRoLoopPatched) {
    return;
  }
  class LoopSafeResizeObserver extends Native {
    constructor(callback: ResizeObserverCallback) {
      let frame = 0;
      super((entries, observer) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => callback(entries, observer));
      });
    }
  }
  (
    LoopSafeResizeObserver as unknown as { __yapiRoLoopPatched: boolean }
  ).__yapiRoLoopPatched = true;
  window.ResizeObserver = LoopSafeResizeObserver;
}

// Dev-only: the loop message is a benign console warning that only matters
// because it trips webpack-dev-server's full-screen overlay in development. In
// production there is no overlay, so we leave the native ResizeObserver timing
// intact rather than deferring every consumer's RO callback to rAF app-wide.
if (process.env.NODE_ENV === "development") {
  patchResizeObserverLoop();
}
